<template>
  <v-dialog v-model="showDialog" max-width="900" scrollable persistent>
    <v-card>
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-truck-plus</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Create Sub Hire Job</span>
        <v-spacer />
        <v-btn icon color="white" small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pt-5 pb-2" v-if="showDialog">
        <v-alert
          v-for="(w, i) in warnings"
          :key="'w' + i"
          dense
          type="warning"
          class="mb-2"
        >
          {{ w }}
        </v-alert>

        <v-alert v-if="formErrors._general" dense type="error" class="mb-2">
          {{ formErrors._general }}
        </v-alert>

        <template v-if="context">
          <v-row class="ma-0 mb-2">
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">Customer</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.customer }}</p>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">Route</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.route_title }}</p>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">Container Type</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.container_type || "-" }}</p>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">Leg</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.leg_id }} ({{ context.anchor_leg.da_no }})</p>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">Container No.</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.container_numbers || "-" }}</p>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <span class="text-caption grey--text">DA Rate</span>
              <p class="mb-0 font-weight-medium">{{ context.anchor_leg.da_rate }}</p>
            </v-col>
          </v-row>

          <v-divider class="mb-3"></v-divider>

          <v-form ref="form" v-model="isValid">
            <v-row class="ma-0">
              <v-col cols="12" md="6" class="py-1">
                <v-select
                  v-if="showVendorSelect"
                  v-model="transporterId"
                  :items="vendorItems"
                  item-text="label"
                  item-value="id"
                  label="Transporter *"
                  outlined
                  dense
                  hide-details="auto"
                  hint="Only transporters with an active contract rate for this route and container type are listed"
                  persistent-hint
                  :rules="[(v) => !!v || 'Required']"
                  @change="onVendorChange"
                />
                <div v-else>
                  <span class="text-caption grey--text">Transporter</span>
                  <p class="mb-0 font-weight-medium">
                    {{ context.detected_vendor.company_name }}
                    <v-btn x-small text color="primary" @click="forceVendorSelect = true">change</v-btn>
                  </p>
                </div>
              </v-col>

              <v-col cols="12" md="6" class="py-1">
                <v-text-field
                  v-model.number="rate"
                  label="Buy Rate (per leg)"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  :hint="rateHint"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" class="py-1">
                <v-textarea
                  v-model="reason"
                  label="Reason for Sub Hire *"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  hide-details="auto"
                  :rules="[(v) => !!(v && v.trim()) || 'Reason is required']"
                />
                <span
                  class="text-caption"
                  :class="willBePending ? 'success--text' : 'warning--text'"
                >
                  {{ willBePending ? "Will create as Pending" : "Will create as Created" }}
                </span>
              </v-col>
            </v-row>
          </v-form>

          <p class="text-caption grey--text mt-3 mb-0">
            The sub-hire is created against this leg only. Add more legs afterwards
            from the sub-hire detail page.
          </p>
        </template>
      </v-card-text>

      <v-divider class="mt-3" />
      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="close" class="mr-2">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!context"
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-check-circle</v-icon>
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ManualSubHireCreateDialog",
  props: {
    value: { type: Boolean, default: false },
    legId: { default: null },
  },
  data() {
    return {
      context: null,
      warnings: [],
      submitting: false,
      isValid: false,
      formErrors: {},

      transporterId: null,
      forceVendorSelect: false,
      rate: null,
      reason: "",
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    showVendorSelect() {
      return this.forceVendorSelect || !this.context || !this.context.detected_vendor;
    },
    vendorItems() {
      const opts = (this.context && this.context.vendor_options) || [];
      const items = opts.map((o) => ({
        id: o.id,
        label: `${o.company_name}${o.rate != null ? " — " + o.rate : " — no rate"}`,
        rate: o.rate,
      }));
      if (this.context && this.context.detected_vendor) {
        const dv = this.context.detected_vendor;
        if (!items.find((i) => i.id === dv.id)) {
          items.unshift({ id: dv.id, label: dv.company_name, rate: this.context.resolved_rate });
        }
      }
      return items;
    },
    willBePending() {
      return !!(this.transporterId && Number(this.rate) > 0);
    },
    rateHint() {
      const opt = this.vendorItems.find((i) => i.id === this.transporterId);
      if (opt && opt.rate != null && Number(this.rate) === Number(opt.rate)) {
        return "Auto-filled from transporter contract";
      }
      return "Enter manually or leave blank";
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.loadContext();
      } else {
        this.reset();
      }
    },
  },
  methods: {
    reset() {
      this.context = null;
      this.warnings = [];
      this.transporterId = null;
      this.forceVendorSelect = false;
      this.rate = null;
      this.reason = "";
      this.formErrors = {};
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.showDialog = false;
    },
    loadContext() {
      this.$bus.$emit("showLoader", true);
      this.$api.subHire
        .getManualCreateContext(this.legId)
        .then((res) => {
          this.context = res.data || res;
          this.warnings = this.context.warnings || [];
          if (this.context.detected_vendor) {
            this.transporterId = this.context.detected_vendor.id;
          }
          if (this.context.resolved_rate != null) {
            this.rate = this.context.resolved_rate;
          }
        })
        .catch((err) => {
          const msg =
            (err.data && (err.data.leg_id || err.data.detail)) ||
            "Couldn't load sub-hire context";
          this.$bus.$emit("showToastMessage", { message: String(msg), color: "error" });
          this.showDialog = false;
        })
        .finally(() => {
          this.$bus.$emit("showLoader", false);
        });
    },
    onVendorChange() {
      const opt = this.vendorItems.find((i) => i.id === this.transporterId);
      if (opt && opt.rate != null) {
        this.rate = opt.rate;
      }
    },
    submit() {
      this.formErrors = {};
      if (this.$refs.form && !this.$refs.form.validate()) return;

      this.submitting = true;
      const payload = {
        anchor_leg_id: this.legId,
        transporter_id: this.transporterId,
        rate: Number(this.rate) > 0 ? this.rate : null,
        reason_for_sub_hire: this.reason,
      };
      this.$api.subHire
        .manualCreateSubHire(payload)
        .then((res) => {
          const data = res.data || res;
          this.$bus.$emit("showToastMessage", {
            message: (data && data.message) || "Sub Hire Job created",
            color: "success",
          });
          this.$emit("refreshList");
          this.showDialog = false;
        })
        .catch((err) => {
          const d = (err && err.data) || {};
          const firstErr =
            d.transporter_id ||
            d.reason_for_sub_hire ||
            d.leg_id ||
            d.detail ||
            "Couldn't create Sub Hire Job";
          this.formErrors._general = Array.isArray(firstErr) ? firstErr.join(", ") : String(firstErr);
          this.$bus.$emit("showToastMessage", {
            message: this.formErrors._general,
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
